import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import { Button, Radio, Space, Spin } from 'udesk-ui';
import { Wrapper, Pad, Title, InteractionPad, Item, Content } from './components';
import ValueList from './components/ValueList';
import { CloseCircleOutlined, CheckboxCircleOutlined } from '@udesk/icons';
import { postOuterApiConfigsTest } from 'src/api/outerApiConfigs/test';
import { OuterApiConfigCreateRequest, OuterApiTestResponseFoundResponse } from 'src/api/types';
import { safeParse } from 'src/util/safeParse';
import UdeskLocales from 'UdeskLocales';

interface TemplateProps {
    data?: OuterApiConfigCreateRequest;
    isTestSuccess?: boolean;
    onTest?: (sucess?: boolean) => void;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { data, isTestSuccess, onTest } = props;

    const [value, setValue] = useState<OuterApiConfigCreateRequest>();

    useEffect(() => {
        setValue((v) => {
            return {
                ...v,
                requestQueryList: data?.requestQueryList?.map((item) => {
                    return {
                        key: item.key,
                        value: undefined,
                    };
                }),
            };
        });
    }, [data?.requestQueryList]);

    useEffect(() => {
        setValue((v) => {
            return {
                ...v,
                requestHeaderList: data?.requestHeaderList?.map((item) => {
                    return {
                        key: item.key,
                        value: undefined,
                    };
                }),
            };
        });
    }, [data?.requestHeaderList]);

    useEffect(() => {
        setValue((v) => {
            return {
                ...v,
                requestBodyList: data?.requestBodyList?.map((item) => {
                    return {
                        key: item.key,
                        value: undefined,
                    };
                }),
            };
        });
    }, [data?.requestBodyList]);

    const [showTest, setShowTest] = useState(false);

    const onTestToggle = () => {
        setShowTest((show) => !show);
    };

    const [curType, setCurType] = useState<'Query' | 'Header' | 'Body'>('Query');

    const onRequestQueryListChange = (v: any) => {
        setValue({
            ...value,
            requestQueryList: v,
        });
    };

    const onRequestHeaderListChange = (v: any) => {
        setValue({
            ...value,
            requestHeaderList: v,
        });
    };

    const onRequestBodyListChange = (v: any) => {
        setValue({
            ...value,
            requestBodyList: v,
        });
    };

    const [testResult, setTestResult] = useState<OuterApiTestResponseFoundResponse>();
    const [isTesting, setIsTesting] = useState(false);

    const onReuqest = () => {
        if (value) {
            setIsTesting(true);
            postOuterApiConfigsTest({
                ...data,
                ...value,
            })
                .then((res) => {
                    setTestResult(res.data);
                    onTest?.(!!res.data?.success);
                })
                .finally(() => {
                    setIsTesting(false);
                });
        }
    };

    const [showInteraction, setShowInteraction] = useState(false);

    const onInteractionToggle = () => {
        setShowInteraction((show) => !show);
    };

    const { requestUrl, requestHeader, requestBody } = useMemo(() => {
        const requestUrl = data?.requestUrl
            ? data?.requestUrl +
              (value?.requestQueryList?.length
                  ? '?' + value?.requestQueryList?.map((q) => `${q.key}=${q.value ?? ''}`).join('&')
                  : '')
            : '';

        const requestHeader = value?.requestHeaderList
            ?.filter((h) => !!h.key)
            .reduce((pre, cur) => {
                return pre + `${cur.key}: ${cur.value ?? ''}\n`;
            }, '');

        const requestBody = JSON.stringify(
            value?.requestBodyList?.reduce((pre, cur) => {
                return { ...pre, [cur.key!]: cur.value };
            }, {}),
            null,
            '\t'
        );

        return {
            requestUrl,
            requestHeader,
            requestBody,
        };
    }, [
        data?.requestUrl,
        value?.requestBodyList,
        value?.requestHeaderList,
        value?.requestQueryList,
    ]);

    const { responseData } = useMemo(() => {
        const responseData = testResult?.apiTestResult?.returnResult
            ? JSON.stringify(safeParse(testResult?.apiTestResult?.returnResult), null, '\t')
            : '';

        return {
            responseData,
        };
    }, [testResult?.apiTestResult?.returnResult]);

    return (
        <Wrapper>
            <Spin spinning={isTesting}>
                <Space align="baseline">
                    <Button type="primary" onClick={onTestToggle} style={{ marginBottom: 10 }}>
                        {/* 测试接口 */}
                        {
                            UdeskLocales['current'].pages.semanticIntelligence.externalInterface
                                .detail.components.interfaceTest.index.testInterface
                        }
                    </Button>

                    {isTestSuccess ? (
                        <Space style={{ color: '#52c41a' }} size="small">
                            <CheckboxCircleOutlined />
                            <span>
                                {/* 测试通过 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .externalInterface.detail.components.interfaceTest.index
                                        .testPassed
                                }
                            </span>
                        </Space>
                    ) : (
                        <Space style={{ color: '#db4a4a' }} size="small">
                            <CloseCircleOutlined />
                            <span>
                                {/* 未测试通过 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .externalInterface.detail.components.interfaceTest.index
                                        .testNotPassed
                                }
                            </span>
                        </Space>
                    )}
                </Space>

                {showTest ? (
                    <Pad>
                        <Radio.Group
                            optionType="button"
                            value={curType}
                            onChange={(e) => setCurType(e.target.value)}
                            options={[
                                {
                                    label: 'Query',
                                    value: 'Query',
                                },
                                {
                                    label: 'Header',
                                    value: 'Header',
                                },
                                {
                                    label: 'Body',
                                    value: 'Body',
                                },
                            ]}
                        />

                        {curType === 'Query' ? (
                            <ValueList
                                title="Query"
                                value={value?.requestQueryList}
                                onChange={onRequestQueryListChange}
                            />
                        ) : curType === 'Header' ? (
                            <ValueList
                                title="Header"
                                value={value?.requestHeaderList}
                                onChange={onRequestHeaderListChange}
                            />
                        ) : curType === 'Body' ? (
                            <ValueList
                                title="Body"
                                value={value?.requestBodyList}
                                onChange={onRequestBodyListChange}
                            />
                        ) : null}

                        <Button
                            type="primary"
                            onClick={onReuqest}
                            size="small"
                            style={{ marginBottom: 20 }}
                        >
                            {/* 请求 */}
                            {
                                UdeskLocales['current'].pages.semanticIntelligence.externalInterface
                                    .detail.components.interfaceTest.index.request
                            }
                        </Button>

                        <Title>
                            {/* 解析后返回结果 */}
                            {
                                UdeskLocales['current'].pages.semanticIntelligence.externalInterface
                                    .detail.components.interfaceTest.index
                                    .resultReturnedAfterParsing
                            }
                        </Title>

                        <ValueList
                            readonly
                            hasBackGround
                            value={testResult?.apiTestResult?.parseReturnResult}
                        />

                        <Button
                            type="link"
                            onClick={onInteractionToggle}
                            style={{ padding: '4px 0' }}
                        >
                            {showInteraction
                                ? /* 收起API交互 */ UdeskLocales['current'].pages
                                      .semanticIntelligence.externalInterface.detail.components
                                      .interfaceTest.index.collapseAPIInteraction
                                : /* 查看API交互 */ UdeskLocales['current'].pages
                                      .semanticIntelligence.externalInterface.detail.components
                                      .interfaceTest.index.viewAPIInteractions}
                        </Button>

                        {showInteraction ? (
                            <InteractionPad>
                                <Item>
                                    <Title>Request URL</Title>
                                    <Content>{requestUrl}</Content>
                                </Item>

                                <Item>
                                    <Title>Request Header</Title>
                                    <Content>{requestHeader}</Content>
                                </Item>

                                <Item>
                                    <Title>Request Body</Title>
                                    <Content>{requestBody}</Content>
                                </Item>

                                <Item>
                                    <Title>Response</Title>
                                    <Content>{responseData}</Content>
                                </Item>
                            </InteractionPad>
                        ) : null}
                    </Pad>
                ) : null}
            </Spin>
        </Wrapper>
    );
});

export default Template;
